import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { GpsInlineLoader } from "@/components/gps-loader";
import {
  Search,
  Plus,
  Lock,
  Settings2,
  Circle,
  MapPin,
  X,
  Layers,
  Pencil,
  Eraser,
  Shapes,
  ChevronDown,
  Check,
  PenTool,
  Users,
  Waypoints,
  Bell,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { resolveVehicleIcon } from "@/lib/vehicle-icons";
import { Switch } from "@/components/ui/switch";
import {
  useVehicles,
  useGeofences,
  useGeofence,
  useCreateGeofence,
  useUpdateGeofence,
  useDeleteGeofence,
  useGeofenceDevices,
  useSyncGeofenceDevices,
  useCreateGeofenceAlertRules,
  useGeofenceAlertRules,
} from "@/hooks/queries";
import type { Geofence, Vehicle } from "@/types";
import type { AlertRule } from "@/services/alert-rules.service";
import { ApiError } from "@/lib/api/client";
import { WaitingBackend } from "@/components/waiting-backend";
import { parseWkt } from "@/lib/wkt";
import { usePermission, usePermissionGateStatus } from "@/hooks/use-permission";
import { LicensePlate } from "@/components/license-plate";

// Pedido 2026-09-01: diferente de Mapa (que nunca some do menu — ver
// app-sidebar.tsx/MapPage), Geocerca continua no menu mas mostra a tela
// "Sem permissão" ao abrir sem geofences.view, mesmo padrão de
// AdminAccessGate/UsersPageGate/SettingsPageGate.
function GeofencesPageGate() {
  // Bug real 2026-09-01: "aparece sem permissões e depois de segundos
  // aparece" — usePermission() sozinho não distingue "ainda carregando"
  // de "negado de verdade" (ver usePermissionGateStatus).
  const status = usePermissionGateStatus("geofences", "view");
  if (status === "loading") {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (status === "denied") {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso para visualizar geocercas. Peça a um administrador para conceder
            a permissão em Usuários → Editar → Permissões.
          </p>
        </div>
      </div>
    );
  }
  return <GeofencesPage />;
}

export const Route = createFileRoute("/geofences")({
  component: GeofencesPageGate,
  head: () => ({
    meta: [
      { title: "Cercas Virtuais — Wp Rastreamento" },
      { name: "description", content: "Gestão de perímetros e cercas virtuais." },
    ],
  }),
});

type TabKey = "todas" | "bloqueio" | "ativas" | "inativas";
type Shape = "circulo" | "poligono" | "retangulo";

// A geometria real do Traccar é sempre WKT em Geofence.area (CIRCLE/POLYGON
// — não existe um tipo "retângulo" nativo; um retângulo desenhado na tela
// é enviado como POLYGON de 4 vértices, e por isso não é distinguível de um
// polígono qualquer depois de recarregado do servidor). A cor não é um
// campo nativo do Traccar — usamos attributes.color (mesma convenção da
// própria interface web do Traccar).
function geofenceColor(g: Geofence): string {
  const c = g.attributes?.color;
  return typeof c === "string" && c ? c : COLORS[0];
}

function geofenceShapeLabel(g: Geofence): Shape {
  return /^\s*CIRCLE/i.test(g.area) ? "circulo" : "poligono";
}

// "Ativa/Inativa" também não é nativo do Traccar (ver comentário acima em
// geofenceColor) — persistido do mesmo jeito que a cor, em attributes,
// então sobrevive a reload e é realmente salvo no servidor via PUT
// /api/geofences/{id}, só que sem existir como coluna própria do Traccar.
function geofenceIsActive(g: Geofence): boolean {
  return g.attributes?.disabled !== true;
}

// Traccar não tem "quantidade de pontos" como campo — é derivado do WKT.
// Círculo não tem vértices próprios (é centro+raio), então mostramos o raio
// em vez de um "1 ponto" sem sentido.
function geofenceGeometryLabel(g: Geofence): string {
  const parsed = parseWkt(g.area);
  if (!parsed) return "—";
  if (parsed.type === "circle") return `Círculo · raio ${Math.round(parsed.data.radius)} m`;
  return `${parsed.data.points.length} pontos`;
}

// AlertRule é por veículo — uma cerca "tem alerta" quando existe ao menos
// uma regra type="geofence" com esse geofenceId (ver GET .../rules/
// geofence-summary). "Com bloqueio" olha o actionKind das regras, não um
// campo próprio da cerca (não existe um "bloqueia" na Traccar).
function groupAlertRulesByGeofence(rules: AlertRule[]): Map<number, AlertRule[]> {
  const map = new Map<number, AlertRule[]>();
  for (const rule of rules) {
    if (rule.geofenceId == null) continue;
    const arr = map.get(rule.geofenceId) ?? [];
    arr.push(rule);
    map.set(rule.geofenceId, arr);
  }
  return map;
}

function hasBlockRule(rules: AlertRule[]): boolean {
  return rules.some((r) => r.actionKind === "block" || r.actionKind === "unblock");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function shapeLayerToWkt(L: any, layer: any): string | null {
  if (layer instanceof L.Circle) {
    const c = layer.getLatLng();
    return `CIRCLE (${c.lng} ${c.lat}, ${layer.getRadius()})`;
  }
  if (layer instanceof L.Polygon) {
    // getLatLngs() pode vir aninhado ([[...]]) dependendo da versão — achata.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let latlngs: any = layer.getLatLngs();
    while (Array.isArray(latlngs[0])) latlngs = latlngs[0];
    if (latlngs.length < 3) return null;
    const closed = [...latlngs, latlngs[0]];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const coords = closed.map((p: any) => `${p.lng} ${p.lat}`).join(", ");
    return `POLYGON ((${coords}))`;
  }
  return null;
}

function GeofencesPage() {
  const [tab, setTab] = useState<TabKey>("todas");
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [alertFor, setAlertFor] = useState<{ id: number; name: string; vehicleIds: string[] } | null>(null);
  // Painel lateral (drawer) aberto ao clicar numa cerca da lista. Guardamos
  // só o id (não o objeto) para que o painel sempre reflita o dado mais
  // recente do cache do React Query, inclusive depois de editar/ativar.
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<number | null>(null);

  // Fonte real: GET /api/geofences (Traccar). Sem mock, sem localStorage,
  // sem fallback.
  const { data: geofencesData, isLoading, isError, error } = useGeofences();
  const deleteGeofence = useDeleteGeofence();
  // Bug real 2026-09-10: botão Excluir aparecia habilitado pra quem não
  // tem geofences:delete — só a tela inteira era gateada (view, em
  // GeofencesPageGate), as ações dentro dela nunca tinham enforcement de
  // frontend (ver comentário em gpsdrive-user-permissions.service.ts).
  const canDeleteGeofences = usePermission("geofences", "delete");
  const geofences = geofencesData ?? [];
  const selectedGeofence = geofences.find((g) => g.id === selectedGeofenceId) ?? null;

  // "Ativa/Inativa" é persistida em attributes.disabled (ver
  // geofenceIsActive) e "Com bloqueio" vem das AlertRule reais dessa cerca
  // (ver groupAlertRulesByGeofence) — nenhum dos dois é um campo nativo do
  // Traccar, mas os dois já existem de verdade hoje, então o filtro/contagem
  // usa o dado real em vez do placeholder "sempre vazio" de antes.
  const { data: geofenceRules = [] } = useGeofenceAlertRules();
  const rulesByGeofence = groupAlertRulesByGeofence(geofenceRules);

  const filtered = geofences.filter((g) => {
    if (query.trim() && !g.name.toLowerCase().includes(query.toLowerCase())) return false;
    if (tab === "ativas") return geofenceIsActive(g);
    if (tab === "inativas") return !geofenceIsActive(g);
    if (tab === "bloqueio") return hasBlockRule(rulesByGeofence.get(g.id) ?? []);
    return true;
  });

  const activeCount = geofences.filter(geofenceIsActive).length;
  const blockCount = geofences.filter((g) => hasBlockRule(rulesByGeofence.get(g.id) ?? [])).length;
  const stats = [
    { label: "Cercas", value: geofences.length, color: "text-slate-900", dot: "bg-emerald-500" },
    { label: "Ativas", value: activeCount, color: "text-slate-900", dot: "bg-emerald-500" },
    { label: "Com bloqueio", value: blockCount, color: "text-rose-600", dot: "bg-rose-500" },
    { label: "Inativas", value: geofences.length - activeCount, color: "text-slate-900", dot: "bg-slate-400" },
  ];

  const tabs: { key: TabKey; label: React.ReactNode }[] = [
    { key: "todas", label: "Todas" },
    {
      key: "bloqueio",
      label: (
        <span className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-amber-500" />
          Com bloqueio
        </span>
      ),
    },
    { key: "ativas", label: "Ativas" },
    { key: "inativas", label: "Inativas" },
  ];


  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex items-center gap-4 border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-sm">
          <Circle className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <h1 className="text-base font-bold leading-tight text-slate-900">Cercas Virtuais</h1>
          <p className="text-xs text-slate-500">gestão de perímetros</p>
        </div>
        <div className="relative ml-4 min-w-0 flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar cerca por nome..."
            className="h-9 w-full min-w-0 rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
        <div className="ml-auto shrink-0">
          <button
            type="button"
            onClick={() => { setEditingGeofence(null); setShowNew(true); }}
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-emerald-500 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            Nova cerca
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 px-6 py-4 md:grid-cols-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
          >
            <span className={cn("h-2 w-2 rounded-full", s.dot)} />
            <div>
              <div className={cn("text-2xl font-bold leading-none", s.color)}>{s.value}</div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mx-6 mb-6 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
                tab === t.key
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {isLoading || isError ? (
          <WaitingBackend error={error} />
        ) : filtered.length === 0 ? (
          <div className="flex h-full items-start justify-center py-16">
            <p className="text-sm text-rose-400/80">Nenhuma cerca criada ainda.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((g) => (
              <li
                key={g.id}
                onClick={() => setSelectedGeofenceId(g.id)}
                className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full ring-2 ring-white shadow"
                  style={{ background: geofenceColor(g) }}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{g.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                    <span className="capitalize">{geofenceShapeLabel(g)}</span>
                  </div>
                </div>
                {(() => {
                  const rules = rulesByGeofence.get(g.id) ?? [];
                  if (rules.length === 0) return null;
                  const blocking = hasBlockRule(rules);
                  return (
                    <span
                      title={`${rules.length} veículo(s) com alerta nesta cerca`}
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold",
                        blocking ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600",
                      )}
                    >
                      {blocking ? <Lock className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                      {blocking ? "Com bloqueio" : "Alerta ativo"}
                    </span>
                  );
                })()}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingGeofence(g); setShowNew(true); }}
                  className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-sky-50 hover:text-sky-600"
                  aria-label="Editar"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canDeleteGeofences) return;
                    if (!confirm(`Excluir a cerca "${g.name}"? Esta ação não pode ser desfeita.`)) return;
                    deleteGeofence.mutate(g.id, {
                      onSuccess: () => toast.success("Cerca excluída", { description: `${g.name} foi removida.` }),
                      onError: (err) => toast.error("Não foi possível excluir", { description: err instanceof ApiError ? err.message : undefined }),
                    });
                  }}
                  disabled={!canDeleteGeofences}
                  className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                  aria-label="Excluir"
                  title={!canDeleteGeofences ? "Sem permissão" : "Excluir"}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        type="button"
        aria-label="Configurações"
        className="fixed bottom-6 right-6 grid h-11 w-11 place-items-center rounded-full bg-sky-500 text-white shadow-lg transition-colors hover:bg-sky-600"
      >
        <Settings2 className="h-5 w-5" />
      </button>

      {showNew && (
        <NewGeofenceModal
          mode={editingGeofence ? "edit" : "create"}
          initial={editingGeofence}
          onClose={() => { setShowNew(false); setEditingGeofence(null); }}
          onSaved={(saved, vehicleIds) => {
            setShowNew(false);
            setEditingGeofence(null);
            setAlertFor({ id: saved.id, name: saved.name, vehicleIds });
          }}
        />
      )}

      {alertFor && (
        <GeofenceAlertModal
          geofenceId={alertFor.id}
          geofenceName={alertFor.name}
          geofenceVehicleIds={alertFor.vehicleIds}
          onClose={() => setAlertFor(null)}
        />
      )}

      <GeofenceDetailsDrawer
        geofence={selectedGeofence}
        onClose={() => setSelectedGeofenceId(null)}
        onEdit={(g) => {
          setSelectedGeofenceId(null);
          setEditingGeofence(g);
          setShowNew(true);
        }}
      />

    </div>
  );
}

// ============================================================================
// Details drawer — opened by clicking a geofence row. Shows details, then
// swaps in-place to the alert-creation form (no navigation away from the
// page). Deliberately built as plain fixed/translate divs — same style as
// NewGeofenceModal/GeofenceAlertModal below — instead of the shadcn Sheet,
// to stay consistent with how the rest of this file already builds
// overlays and avoid re-deriving Radix Dialog z-index/focus-trap quirks.
// ============================================================================

function GeofenceDetailsDrawer({
  geofence,
  onClose,
  onEdit,
}: {
  geofence: Geofence | null;
  onClose: () => void;
  onEdit: (g: Geofence) => void;
}) {
  const isOpen = !!geofence;
  const [view, setView] = useState<"details" | "alert">("details");
  const updateGeofence = useUpdateGeofence();
  const deleteGeofence = useDeleteGeofence();
  const canDeleteGeofences = usePermission("geofences", "delete");
  const { data: allDevices = [] } = useVehicles();
  const deviceIds = allDevices.map((v) => Number(v.id)).filter((id) => Number.isFinite(id));
  const { data: linkedDeviceIds = [] } = useGeofenceDevices(geofence?.id, deviceIds, isOpen);
  // Mesma query que a listagem usa (React Query dedupe — sem chamada
  // extra); filtrada aqui pela cerca aberta, pra saber se já existe alerta
  // configurado (pré-preencher o painel, mudar "Criar" → "Editar").
  const { data: allGeofenceRules = [] } = useGeofenceAlertRules();
  const rulesForGeofence = geofence ? allGeofenceRules.filter((r) => r.geofenceId === geofence.id) : [];

  // Sempre volta para "detalhes" ao trocar de cerca (ou fechar/reabrir).
  useEffect(() => {
    setView("details");
  }, [geofence?.id]);

  const handleToggleActive = () => {
    if (!geofence) return;
    const nextDisabled = geofenceIsActive(geofence);
    updateGeofence.mutate(
      { id: geofence.id, data: { ...geofence, attributes: { ...geofence.attributes, disabled: nextDisabled } } },
      {
        onSuccess: () =>
          toast.success(nextDisabled ? "Cerca desativada" : "Cerca ativada", { description: geofence.name }),
        onError: (err) =>
          toast.error("Não foi possível atualizar a cerca", {
            description: err instanceof ApiError ? err.message : undefined,
          }),
      },
    );
  };

  const handleDelete = () => {
    if (!geofence || !canDeleteGeofences) return;
    if (!confirm(`Excluir a cerca "${geofence.name}"? Esta ação não pode ser desfeita.`)) return;
    deleteGeofence.mutate(geofence.id, {
      onSuccess: () => {
        toast.success("Cerca excluída", { description: `${geofence.name} foi removida.` });
        onClose();
      },
      onError: (err) =>
        toast.error("Não foi possível excluir", { description: err instanceof ApiError ? err.message : undefined }),
    });
  };

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 z-40 bg-slate-900/40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        className={cn(
          "geofence-drawer z-50 flex flex-col bg-white shadow-2xl transition-transform duration-300 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {geofence && view === "details" && (
          <GeofenceDetailsView
            geofence={geofence}
            vehicleCount={linkedDeviceIds.length}
            hasAlert={rulesForGeofence.length > 0}
            blocking={hasBlockRule(rulesForGeofence)}
            onClose={onClose}
            onEdit={() => onEdit(geofence)}
            onDelete={handleDelete}
            canDelete={canDeleteGeofences}
            onToggleActive={handleToggleActive}
            toggling={updateGeofence.isPending}
            onCreateAlert={() => setView("alert")}
          />
        )}
        {geofence && view === "alert" && (
          <GeofenceAlertPanel
            geofenceId={geofence.id}
            geofenceName={geofence.name}
            existingRules={rulesForGeofence}
            geofenceVehicleIds={linkedDeviceIds}
            onBack={() => setView("details")}
            onClose={onClose}
          />
        )}
      </aside>
    </>
  );
}

function GeofenceDetailsView({
  geofence,
  vehicleCount,
  hasAlert,
  blocking,
  onClose,
  onEdit,
  onDelete,
  canDelete,
  onToggleActive,
  toggling,
  onCreateAlert,
}: {
  geofence: Geofence;
  vehicleCount: number;
  hasAlert: boolean;
  blocking: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canDelete: boolean;
  onToggleActive: () => void;
  toggling: boolean;
  onCreateAlert: () => void;
}) {
  const active = geofenceIsActive(geofence);
  const group = typeof geofence.attributes?.group === "string" ? (geofence.attributes.group as string) : null;

  return (
    <>
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-white shadow-sm"
          style={{ background: geofenceColor(geofence) }}
        >
          <Circle className="h-4 w-4" fill="white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-bold text-slate-900">{geofence.name}</h2>
          <p className="mt-0.5 text-xs capitalize text-slate-500">{geofenceShapeLabel(geofence)}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Status */}
        <section className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="min-w-0">
            <div className={cn("text-sm font-semibold", active ? "text-emerald-700" : "text-slate-500")}>
              {active ? "Cerca ativa" : "Cerca desativada"}
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {active ? "Monitorando entradas e saídas" : "Sem monitoramento no momento"}
            </div>
          </div>
          <Switch
            checked={active}
            disabled={toggling}
            onCheckedChange={onToggleActive}
            aria-label="Ativar ou desativar cerca"
          />
        </section>

        {/* Info grid */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <Users className="h-3 w-3" /> Grupo
            </div>
            <div className="mt-1 truncate text-sm font-semibold text-slate-800">{group ?? "Sem grupo"}</div>
          </div>
          <div className="rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <Waypoints className="h-3 w-3" /> Pontos
            </div>
            <div className="mt-1 truncate text-sm font-semibold text-slate-800">
              {geofenceGeometryLabel(geofence)}
            </div>
          </div>
          <div className="col-span-2 rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <MapPin className="h-3 w-3" /> Veículos monitorados
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-800">{vehicleCount}</div>
          </div>
        </div>

        {/* O que essa cerca faz */}
        <section className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">O que esta cerca faz</h3>
          <p className="mt-1.5 text-sm text-slate-600">
            {geofence.description?.trim() ||
              "Você será avisado quando um veículo monitorado entrar ou sair desta área."}
          </p>
          {hasAlert && (
            <div
              className={cn(
                "mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
                blocking ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600",
              )}
            >
              {blocking ? <Lock className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
              {blocking ? "Alerta com bloqueio automático configurado" : "Alerta configurado"}
            </div>
          )}
        </section>

        {/* Ações */}
        <div className={cn("mt-4 grid gap-2", hasAlert ? "grid-cols-3" : "grid-cols-2")}>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Pencil className="h-3.5 w-3.5" /> Editar cerca
          </button>
          {hasAlert && (
            <button
              type="button"
              onClick={onCreateAlert}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <Bell className="h-3.5 w-3.5" /> Editar ação
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            title={!canDelete ? "Sem permissão" : undefined}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
          >
            <X className="h-3.5 w-3.5" /> Excluir
          </button>
        </div>
      </div>

      <div className="border-t border-slate-100 bg-white px-5 py-3">
        <button
          type="button"
          onClick={onCreateAlert}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600"
        >
          <Bell className="h-4 w-4" />
          {hasAlert ? "Editar alerta desta cerca" : "Criar alerta para esta cerca"}
        </button>
      </div>
    </>
  );
}

// ============================================================================
// New geofence — fullscreen modal with side panel + map
// ============================================================================

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#ef4444",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
];

type LayerKey = "streets" | "satellite" | "hybrid";
const TILES: Record<LayerKey, { label: string; url: string; sub: string }> = {
  streets: {
    label: "Mapa",
    url: "https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=pt-BR&scale=2",
    sub: "0123",
  },
  satellite: {
    label: "Satélite HD",
    url: "https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&scale=2",
    sub: "0123",
  },
  hybrid: {
    label: "Híbrido HD",
    url: "https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&hl=pt-BR&scale=2",
    sub: "0123",
  },
};

export function NewGeofenceModal({
  onClose,
  onSaved,
  mode = "create",
  initial = null,
}: {
  onClose: () => void;
  // vehicleIds = veículos marcados na cerca — o popup de alerta que abre em
  // seguida já começa com eles selecionados (ver GeofenceAlertModal).
  onSaved: (saved: Geofence, vehicleIds: string[]) => void;
  mode?: "create" | "edit";
  initial?: Geofence | null;
}) {
  const isEdit = mode === "edit";
  const createGeofence = useCreateGeofence();
  const updateGeofence = useUpdateGeofence();
  const syncGeofenceDevices = useSyncGeofenceDevices();
  const editId = isEdit ? initial?.id : undefined;
  // Ao editar, busca a geocerca fresca no Traccar (GET /api/geofences/{id})
  // em vez de reaproveitar só o dado já em cache da listagem.
  const { data: freshGeofence, isLoading: loadingGeofence } = useGeofence(editId);
  const { data: allDevices = [] } = useVehicles();
  // Filtra veículos "extras" (adicionados só localmente, nunca cadastrados
  // no Traccar — id não numérico) antes de consultar vínculo de geocerca:
  // eles não existem no Traccar, não podem ter vínculo real nenhum, e um id
  // inválido aqui não pode nunca mais atrapalhar a leitura dos demais.
  const deviceIds = allDevices.map((v) => Number(v.id)).filter((id) => Number.isFinite(id));
  const { data: linkedDeviceIds, isLoading: loadingDeviceLinks } = useGeofenceDevices(
    isEdit ? editId : undefined,
    deviceIds,
  );
  const [saving, setSaving] = useState(false);
  const mapRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const leafletMapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tileRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markersRef = useRef<Record<string, any>>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shapesRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const drawingRef = useRef<{ start?: any; preview?: any; points?: any[]; previewLine?: any }>({});
  const [mapReady, setMapReady] = useState(false);

  const [layer, setLayer] = useState<LayerKey>("streets");
  const [showLayers, setShowLayers] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  // Guarda de "usuário já mexeu num checkbox" — ver comentário no efeito de
  // sincronização abaixo (perto de linkedDeviceIds).
  const userEditedSelectionRef = useRef(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [shape, setShape] = useState<Shape>("circulo");
  const [showShapes, setShowShapes] = useState(false);
  const [tool, setTool] = useState<"draw" | "erase" | null>(null);

  // Seleção/edição de vértices da forma atual (a geocerca pré-carregada em
  // modo de edição, ou a forma recém-desenhada em modo de criação). Clicar
  // só seleciona/destaca; vértices (via Leaflet-Geoman) só aparecem depois
  // de "Editar forma".
  const [shapeSelected, setShapeSelected] = useState(false);
  const [shapeEditing, setShapeEditing] = useState(false);
  const [hasActiveShape, setHasActiveShape] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeShapeRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editBackupRef = useRef<any>(null);
  const shapeEditingRef = useRef(false);
  useEffect(() => {
    shapeEditingRef.current = shapeEditing;
  }, [shapeEditing]);

  useEffect(() => {
    let cancelled = false;
    if (!mapRef.current || leafletMapRef.current) return;
    (async () => {
      // CSS do Leaflet/Geoman só aqui (única tela que ainda usa Leaflet) —
      // antes vinha do unpkg.com no <head> do __root, bloqueando a pintura
      // de TODAS as telas até a CDN externa responder (tela preta ao abrir,
      // pedido 2026-09-23). Agora sai do nosso próprio bundle, sob demanda.
      await Promise.all([
        import("leaflet/dist/leaflet.css"),
        import("@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css"),
      ]);
      const L = await import("leaflet");
      await import("@geoman-io/leaflet-geoman-free");
      if (cancelled || !mapRef.current) return;
      LRef.current = L;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const map = (L as any).map(mapRef.current, {
        center: [-14.2, -51.9],
        zoom: 4,
        zoomControl: false,
        attributionControl: false,
      });
      const def = TILES.streets;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tileRef.current = (L as any).tileLayer(def.url, { subdomains: def.sub, maxZoom: 21 }).addTo(map);
      leafletMapRef.current = map;
      setTimeout(() => map.invalidateSize(), 100);
      setMapReady(true);
    })();
    return () => {
      cancelled = true;
      leafletMapRef.current?.remove?.();
      leafletMapRef.current = null;
      markersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const L = LRef.current;
    const map = leafletMapRef.current;
    if (!L || !map) return;
    if (tileRef.current) map.removeLayer(tileRef.current);
    const def = TILES[layer];
    tileRef.current = L.tileLayer(def.url, { subdomains: def.sub, maxZoom: 21 }).addTo(map);
  }, [layer]);

  // Sync markers with selected vehicles
  useEffect(() => {
    const L = LRef.current;
    const map = leafletMapRef.current;
    if (!L || !map || !mapReady) return;

    // Remove markers no longer selected
    for (const id of Object.keys(markersRef.current)) {
      if (!selectedVehicles.includes(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    }
    // Add markers for newly selected
    for (const id of selectedVehicles) {
      if (markersRef.current[id]) continue;
      const v = allDevices.find((x) => x.id === id);
      if (!v) continue;
      const iconResolved = resolveVehicleIcon(v);
      const speed = Math.max(0, Math.round(v.speed ?? 0));
      const rot = ((v as unknown as { bearing?: number }).bearing ?? 0).toFixed(1);
      const art = `<span class="wp-veh-photo" data-vid="${v.id}">
          <span class="wp-veh-photo-img" style="--art:url('${iconResolved.url}'); transform:rotate(${rot}deg); transform-origin:50% 50%;"></span>
        </span>`;
      const lock = v.blocked
        ? `<span class="wp-lock" title="Bloqueado"><svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg></span>`
        : "";
      const speedPill = `<span class="wp-speed-pill">${speed}<em>km/h</em></span>`;
      const icon = L.divIcon({
        className: "wp-marker",
        html: `<div class="wp-pin3d wp-pin3d--clean">${art}${lock}${speedPill}</div>`,
        iconSize: [56, 72],
        iconAnchor: [28, 36],
      });
      markersRef.current[id] = L.marker([v.lat, v.lng], { icon }).addTo(map);
    }
  }, [selectedVehicles, mapReady, allDevices]);

  const toggleVehicle = (id: string) => {
    userEditedSelectionRef.current = true;
    setSelectedVehicles((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Zoom to vehicle if being added
      if (!prev.includes(id)) {
        const v = allDevices.find((x) => x.id === id);
        const map = leafletMapRef.current;
        if (v && map) {
          map.flyTo([v.lat, v.lng], 16, { duration: 0.8 });
        }
      }
      return next;
    });
  };

  // Torna uma camada (nova ou pré-carregada) a "forma ativa": clique só
  // seleciona/destaca (nunca entra em edição sozinho); com a ferramenta
  // Apagar ativa, clique remove a camada normalmente.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attachShapeSelect = (layer: any, map: any) => {
    activeShapeRef.current = layer;
    setHasActiveShape(true);
    layer.on("click", () => {
      if ((drawingRef.current as any)?._erasing) {
        map.removeLayer(layer);
        shapesRef.current = shapesRef.current.filter((s) => s !== layer);
        if (activeShapeRef.current === layer) {
          activeShapeRef.current = null;
          setHasActiveShape(false);
          setShapeSelected(false);
          setShapeEditing(false);
        }
        return;
      }
      if (shapeEditingRef.current) return;
      setShapeSelected((prev) => !prev);
    });
  };

  // Drawing & erasing tool
  useEffect(() => {
    const L = LRef.current;
    const map = leafletMapRef.current;
    if (!L || !map || !mapReady) return;

    // Reset any in-progress preview when tool/shape/color changes
    const d = drawingRef.current;
    if (d.preview) { map.removeLayer(d.preview); d.preview = undefined; }
    if (d.previewLine) { map.removeLayer(d.previewLine); d.previewLine = undefined; }
    d.start = undefined;
    d.points = undefined;

    const container = map.getContainer() as HTMLElement;
    container.style.cursor = tool === "draw" ? "crosshair" : tool === "erase" ? "not-allowed" : "";

    if (!tool) return;

    const style = { color, weight: 2, fillColor: color, fillOpacity: 0.25 };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addShape = (layer: any) => {
      shapesRef.current.push(layer);
      attachShapeSelect(layer, map);
    };

    if (tool === "erase") {
      (drawingRef.current as any)._erasing = true;
      return () => {
        (drawingRef.current as any)._erasing = false;
        container.style.cursor = "";
      };
    }
    (drawingRef.current as any)._erasing = false;

    // Polígono usa o desenho nativo do Leaflet-Geoman: cada clique cria um
    // vértice visível ("bolinha"), igual ao editor oficial do Traccar, em
    // vez da linha tracejada manual usada antes. Termina com duplo-clique
    // ou clicando de volta no primeiro vértice.
    if (shape === "poligono") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onCreate = (e: any) => {
        if (e.shape !== "Polygon") return;
        const poly = e.layer;
        poly.setStyle?.(style);
        addShape(poly);
        setTool(null);
      };
      map.on("pm:create", onCreate);
      map.pm.enableDraw("Polygon", {
        templineStyle: style,
        hintlineStyle: style,
        pathOptions: style,
        allowSelfIntersection: false,
      });
      return () => {
        map.off("pm:create", onCreate);
        map.pm.disableDraw("Polygon");
        container.style.cursor = "";
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onClick = (e: any) => {
      const latlng = e.latlng;
      if (shape === "retangulo") {
        if (!d.start) {
          d.start = latlng;
          d.preview = L.rectangle([latlng, latlng], { ...style, dashArray: "4 4" }).addTo(map);
        } else {
          if (d.preview) map.removeLayer(d.preview);
          const rect = L.rectangle([d.start, latlng], style).addTo(map);
          addShape(rect);
          d.start = undefined;
          d.preview = undefined;
        }
      } else if (shape === "circulo") {
        if (!d.start) {
          d.start = latlng;
          d.preview = L.circle(latlng, { radius: 1, ...style, dashArray: "4 4" }).addTo(map);
        } else {
          if (d.preview) map.removeLayer(d.preview);
          const r = map.distance(d.start, latlng);
          const c = L.circle(d.start, { radius: r, ...style }).addTo(map);
          addShape(c);
          d.start = undefined;
          d.preview = undefined;
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onMouseMove = (e: any) => {
      const latlng = e.latlng;
      if (shape === "retangulo" && d.start && d.preview) {
        d.preview.setBounds([d.start, latlng]);
      } else if (shape === "circulo" && d.start && d.preview) {
        d.preview.setRadius(map.distance(d.start, latlng));
      }
    };

    map.doubleClickZoom.disable();
    map.on("click", onClick);
    map.on("mousemove", onMouseMove);

    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMouseMove);
      map.doubleClickZoom.enable();
      container.style.cursor = "";
      if (d.preview) { map.removeLayer(d.preview); d.preview = undefined; }
      d.start = undefined;
    };
  }, [tool, shape, color, mapReady]);

  // Pré-preenche nome/cor e desenha a área real (WKT vinda do Traccar) ao
  // editar uma geocerca existente.
  const prefillDoneRef = useRef(false);
  useEffect(() => {
    if (!isEdit || !freshGeofence || !mapReady) return;
    if (prefillDoneRef.current) return;
    const L = LRef.current;
    const map = leafletMapRef.current;
    if (!L || !map) return;

    setName(freshGeofence.name ?? "");
    const attrColor = freshGeofence.attributes?.color;
    const drawColor = typeof attrColor === "string" && attrColor ? attrColor : color;
    if (typeof attrColor === "string" && attrColor) setColor(attrColor);

    const parsed = parseWkt(freshGeofence.area);
    if (parsed) {
      const style = { color: drawColor, weight: 2, fillColor: drawColor, fillOpacity: 0.25 };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let layer: any;
      if (parsed.type === "circle") {
        setShape("circulo");
        layer = L.circle([parsed.data.lat, parsed.data.lng], { radius: parsed.data.radius, ...style }).addTo(map);
        map.setView([parsed.data.lat, parsed.data.lng], 15);
      } else {
        setShape("poligono");
        const latlngs = parsed.data.points.map((p) => [p.lat, p.lng]);
        layer = L.polygon(latlngs, style).addTo(map);
        map.fitBounds(layer.getBounds(), { padding: [40, 40] });
      }
      shapesRef.current.push(layer);
      attachShapeSelect(layer, map);
    }
    prefillDoneRef.current = true;
  }, [isEdit, freshGeofence, mapReady, color]);

  // Destaque leve ao selecionar/editar — nunca mostra vértices sozinho.
  useEffect(() => {
    const layer = activeShapeRef.current;
    if (!layer?.setStyle) return;
    layer.setStyle({ weight: shapeEditing || shapeSelected ? 3 : 2 });
  }, [shapeSelected, shapeEditing]);

  const handleEditShape = () => {
    const layer = activeShapeRef.current;
    const L = LRef.current;
    if (!layer?.pm || !L) return;
    if (layer instanceof L.Circle) {
      editBackupRef.current = { type: "circle", latlng: layer.getLatLng(), radius: layer.getRadius() };
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let latlngs: any = layer.getLatLngs();
      while (Array.isArray(latlngs[0])) latlngs = latlngs[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      editBackupRef.current = { type: "polygon", latlngs: latlngs.map((p: any) => L.latLng(p.lat, p.lng)) };
    }
    setTool(null);
    // draggable: false — só os vértices (bolinhas) podem ser movidos, não a
    // forma inteira arrastando o preenchimento/contorno.
    layer.pm.enable({ allowSelfIntersection: false, draggable: false });
    setShapeEditing(true);
    setShapeSelected(false);
  };

  const handleCancel = () => {
    const layer = activeShapeRef.current;
    const backup = editBackupRef.current;
    if (shapeEditing && layer && backup) {
      if (backup.type === "circle") {
        layer.setLatLng(backup.latlng);
        layer.setRadius(backup.radius);
      } else {
        layer.setLatLngs(backup.latlngs);
      }
      layer.pm?.disable?.();
    }
    onClose();
  };

  // Marca automaticamente os veículos já vinculados a esta geocerca (estado
  // atual real, via GET /api/geofences?deviceId= por dispositivo).
  //
  // A guarda é em CIMA DE INTERAÇÃO DO USUÁRIO, não em cima de "já chegou
  // dado uma vez": o React Query serve o valor em cache instantaneamente ao
  // montar (mesmo estando stale) e só atualiza para o valor fresco alguns
  // instantes depois, em segundo plano. Uma versão anterior desta guarda
  // travava no primeiro valor visto — geralmente o cache stale de ANTES do
  // último salvamento — e ignorava a correção que chegava logo em seguida,
  // fazendo o checkbox voltar a mostrar o estado de antes de salvar. Aqui,
  // continuamos sincronizando com todo valor novo do servidor até o usuário
  // efetivamente mexer num checkbox — só a partir daí paramos, para não
  // apagar uma edição em andamento por causa de um refetch em segundo
  // plano (ex.: refetchOnWindowFocus).
  useEffect(() => {
    if (!isEdit || !linkedDeviceIds) return;
    if (userEditedSelectionRef.current) return;
    setSelectedVehicles(linkedDeviceIds);
  }, [isEdit, linkedDeviceIds]);

  const handleSaveGeofence = async () => {
    if (!name.trim()) return;
    if (activeShapeRef.current?.pm?.enabled?.()) {
      activeShapeRef.current.pm.disable();
    }
    setShapeEditing(false);
    setShapeSelected(false);
    const L = LRef.current;
    const lastShape = shapesRef.current[shapesRef.current.length - 1];
    if (!L || !lastShape) {
      toast.error("Desenhe uma área no mapa antes de salvar.");
      return;
    }
    const area = shapeLayerToWkt(L, lastShape);
    if (!area) {
      toast.error("Não foi possível calcular a área desenhada.");
      return;
    }

    setSaving(true);
    const payload: Partial<Geofence> = {
      name: name.trim(),
      area,
      attributes: { ...(isEdit ? freshGeofence?.attributes : undefined), color },
    };

    try {
      const saved =
        isEdit && initial
          ? await updateGeofence.mutateAsync({ id: initial.id, data: { ...freshGeofence, ...payload } })
          : await createGeofence.mutateAsync(payload);

      // Diff entre o vínculo atual (baseline) e o selecionado — só as
      // diferenças são enviadas, nunca apaga tudo para recriar.
      const baseline = new Set(isEdit ? (linkedDeviceIds ?? []) : []);
      const selected = new Set(selectedVehicles);
      const link = [...selected].filter((id) => !baseline.has(id)).map(Number);
      const unlink = [...baseline].filter((id) => !selected.has(id)).map(Number);
      if (link.length || unlink.length) {
        await syncGeofenceDevices.mutateAsync({ geofenceId: saved.id, link, unlink });
      }

      toast.success(isEdit ? "Cerca atualizada" : "Cerca criada", {
        description: `${saved.name} foi ${isEdit ? "atualizada" : "salva"}.`,
      });
      onSaved(saved, selectedVehicles);
    } catch (err) {
      toast.error(isEdit ? "Não foi possível atualizar a cerca" : "Não foi possível criar a cerca", {
        description: err instanceof ApiError ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex bg-slate-100">
      {/* Side panel */}
      <aside className="relative z-10 flex w-[340px] shrink-0 flex-col border-r border-slate-200 bg-white shadow-xl">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-rose-50 text-rose-500">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900">Cercas e Perímetros</h2>
            <p className="text-xs text-slate-500">Seja avisado quando o veículo entrar ou sair</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Veículos */}
          <section>
            <h3 className="text-sm font-bold text-slate-900">Veículos</h3>
            <p className="mt-1 text-xs text-slate-500">
              Escolha um veículo para vê-lo no mapa e desenhar a cerca ao redor dele.
            </p>
            <div className="relative mt-2">
              <button
                type="button"
                onClick={() => setShowVehicles((s) => !s)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:border-slate-300"
              >
                <span className={cn(selectedVehicles.length === 0 && "text-slate-400")}>
                  {selectedVehicles.length === 0
                    ? "Toque para escolher um ou mais"
                    : `${selectedVehicles.length} veículo(s) selecionado(s)`}
                </span>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
              {showVehicles && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                  {allDevices.map((v) => {
                    const on = selectedVehicles.includes(v.id);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => toggleVehicle(v.id)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <span
                          className={cn(
                            "grid h-4 w-4 shrink-0 place-items-center rounded border",
                            on ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white",
                          )}
                        >
                          {on && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="truncate text-slate-700">{v.name}</span>
                        <LicensePlate plate={v.plate} size="xs" className="ml-auto" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Localização */}
          <section className="mt-5">
            <h3 className="text-sm font-bold text-slate-900">Localização da área</h3>
            <p className="mt-1 text-xs text-slate-500">
              Pesquise um endereço ou delimite a área diretamente no mapa.
            </p>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-500" />
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Endereço, cidade ou ponto (lat, lng)"
                className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {hasActiveShape && (
                <button
                  type="button"
                  onClick={handleEditShape}
                  disabled={shapeEditing}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
                    shapeEditing
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                  )}
                >
                  <PenTool className="h-3.5 w-3.5 text-violet-500" />
                  {shapeEditing ? "Editando vértices" : "Editar forma"}
                </button>
              )}
              <button
                type="button"
                disabled={shapeEditing}
                onClick={() => setTool(tool === "draw" ? null : "draw")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                  tool === "draw"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                )}
              >
                <Pencil className="h-3.5 w-3.5 text-emerald-500" />
                Lápis
              </button>
              <button
                type="button"
                disabled={shapeEditing}
                onClick={() => setTool(tool === "erase" ? null : "erase")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                  tool === "erase"
                    ? "border-amber-500 bg-amber-50 text-amber-700"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                )}
              >
                <Eraser className="h-3.5 w-3.5 text-amber-500" />
                Apagar
              </button>
              <div className="relative">
                <button
                  type="button"
                  disabled={shapeEditing}
                  onClick={() => setShowShapes((s) => !s)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Shapes className="h-3.5 w-3.5 text-sky-500" />
                  Formas
                  <ChevronDown className="h-3 w-3" />
                </button>
                {showShapes && (
                  <div className="absolute z-20 mt-1 w-36 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
                    {(["circulo", "poligono", "retangulo"] as Shape[]).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setShape(s);
                          setShowShapes(false);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2 px-3 py-2 text-left text-xs capitalize",
                          shape === s ? "bg-sky-50 font-semibold text-sky-700" : "text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        {s}
                        {shape === s && <Check className="ml-auto h-3 w-3" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Nome */}
          <section className="mt-5">
            <label className="text-sm font-bold text-slate-900">
              Nome da área <span className="text-xs font-normal text-slate-500">(obrigatório)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Casa, Sítio, Trabalho..."
              className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </section>

          {/* Cor */}
          <section className="mt-5">
            <h3 className="text-sm font-bold text-slate-900">Cor da área no mapa</h3>
            <div className="mt-2 flex items-center gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={cn(
                    "grid h-8 w-8 place-items-center rounded-full transition-transform",
                    color === c ? "ring-2 ring-slate-900 ring-offset-2" : "hover:scale-110",
                  )}
                  style={{ background: c }}
                  aria-label={`Cor ${c}`}
                >
                  {color === c && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                </button>
              ))}
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-full"
                style={{
                  background: "conic-gradient(from 0deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ec4899, #ef4444)",
                }}
                aria-label="Personalizado"
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            ◀ Cancelar
          </button>
          <button
            type="button"
            disabled={!name.trim() || saving || (isEdit && (loadingGeofence || loadingDeviceLinks))}
            onClick={handleSaveGeofence}
            className="inline-flex flex-1 items-center justify-center rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Salvando…" : "Salvar área"}
          </button>
        </div>
      </aside>

      {/* Map */}
      <div className="relative flex-1">
        <div ref={mapRef} className="absolute inset-0" />

        <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowLayers((s) => !s)}
              className="grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-lg hover:bg-slate-50"
              aria-label="Camadas"
            >
              <Layers className="h-4 w-4" />
            </button>
            {showLayers && (
              <div className="absolute right-0 top-12 w-40 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
                {(Object.keys(TILES) as LayerKey[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => {
                      setLayer(k);
                      setShowLayers(false);
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-xs",
                      layer === k ? "bg-sky-50 font-semibold text-sky-700" : "text-slate-700 hover:bg-slate-50",
                    )}
                  >
                    {TILES[k].label}
                    {layer === k && <Check className="ml-auto h-3 w-3" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-lg hover:bg-slate-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          aria-label="Configurações"
          className="absolute bottom-6 right-6 grid h-11 w-11 place-items-center rounded-full bg-sky-500 text-white shadow-lg hover:bg-sky-600"
        >
          <Settings2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Post-save alert popup
// ============================================================================

type TriggerWhen = "sair" | "entrar" | "ambos";
type ActionKind = "avisar" | "bloquear" | "desbloquear";

// Vocabulário da tela (PT-BR) → vocabulário do backend (GPS Drive
// Backend, notifications.dto.ts) — só os rótulos mudam, o significado é
// o mesmo.
function triggerWhenToApi(when: TriggerWhen): "enter" | "exit" | "both" {
  return when === "sair" ? "exit" : when === "entrar" ? "enter" : "both";
}
function actionKindToApi(action: ActionKind): "notify" | "block" | "unblock" {
  return action === "avisar" ? "notify" : action === "bloquear" ? "block" : "unblock";
}
function triggerWhenFromApi(when: "enter" | "exit" | "both" | null): TriggerWhen {
  return when === "exit" ? "sair" : when === "enter" ? "entrar" : "ambos";
}
function actionKindFromApi(action: "notify" | "block" | "unblock"): ActionKind {
  return action === "block" ? "bloquear" : action === "unblock" ? "desbloquear" : "avisar";
}

interface GeofenceAlertExisting {
  vehicleIds: string[];
  when: TriggerWhen;
  action: ActionKind;
  name: string;
  notifyApp: boolean;
  notifyEmail: boolean;
}

// Reconstrói o estado do formulário a partir das AlertRule já salvas para
// esta cerca (uma por veículo, ver groupAlertRulesByGeofence) — sem isso,
// reabrir "Criar alerta" para uma cerca que já tem alerta configurado
// mostrava sempre os valores em branco, dando a impressão de que o que
// foi salvo antes (ex.: bloquear) tinha sumido.
function buildExistingGeofenceAlertConfig(rules: AlertRule[], allDevices: Vehicle[]): GeofenceAlertExisting | null {
  if (rules.length === 0) return null;
  const representative = rules[0];
  const ruleDeviceIds = new Set(rules.map((r) => r.traccarDeviceId));
  const allSelected = allDevices.length > 0 && allDevices.every((v) => ruleDeviceIds.has(Number(v.id)));
  return {
    vehicleIds: allSelected ? [] : rules.map((r) => String(r.traccarDeviceId)),
    when: triggerWhenFromApi(representative.triggerWhen),
    action: actionKindFromApi(representative.actionKind),
    name: representative.name ?? "",
    notifyApp: representative.channelApp,
    notifyEmail: representative.channelEmail,
  };
}

// vehicleIds vazio no formulário representa "Todos os veículos" (ver
// texto do botão em GeofenceAlertFormFields) — resolvido pra a lista real
// de ids só no momento de enviar, nunca guardado como estado.
function resolveGeofenceAlertDeviceIds(form: GeofenceAlertFormState, allDevices: Vehicle[]): number[] {
  const ids = form.vehicleIds.length > 0 ? form.vehicleIds : allDevices.map((v) => v.id);
  return ids.map(Number).filter((id) => Number.isFinite(id));
}

// Estado do formulário de alerta, compartilhado entre o popup pós-criação
// (GeofenceAlertModal) e o painel embutido no drawer (GeofenceAlertPanel) —
// mesmos campos, dois lugares diferentes de onde acioná-lo.
// `geofenceVehicleIds` = veículos vinculados à cerca. Bug relatado 2026-09-28:
// o formulário começava sempre vazio (= "Todos os veículos") mesmo com um
// veículo marcado na cerca — e com "Bloquear" isso vale pra frota inteira.
// Agora, sem alerta salvo, começa com os veículos da própria cerca.
function useGeofenceAlertFormState(
  geofenceName: string,
  allDevices: Vehicle[],
  existing?: GeofenceAlertExisting | null,
  geofenceVehicleIds?: string[],
) {
  const [vehicleIds, setVehicleIds] = useState<string[]>(existing?.vehicleIds ?? geofenceVehicleIds ?? []);
  // Vínculos da cerca chegam depois (query) quando o painel abre direto —
  // aplica uma vez, só se ainda não há alerta salvo nem escolha do usuário.
  const vehicleIdsTouchedRef = useRef(!!existing);
  useEffect(() => {
    if (vehicleIdsTouchedRef.current || !geofenceVehicleIds?.length) return;
    setVehicleIds(geofenceVehicleIds);
  }, [geofenceVehicleIds]);
  const [showList, setShowList] = useState(false);
  const [search, setSearch] = useState("");
  const [when, setWhen] = useState<TriggerWhen>(existing?.when ?? "sair");
  const [action, setAction] = useState<ActionKind>(existing?.action ?? "avisar");
  const [alertName, setAlertName] = useState(
    existing?.name ||
      `${geofenceName} — ao ${when === "sair" ? "SAIR" : when === "entrar" ? "ENTRAR" : "entrar ou sair"}`,
  );
  // Já existe um alerta salvo: mantém o nome exatamente como está gravado
  // em vez de reescrevê-lo quando "quando disparar" mudar (efeito abaixo).
  const [nameTouched, setNameTouched] = useState(!!existing);
  const [notifyApp, setNotifyApp] = useState(existing?.notifyApp ?? true);
  const [notifyEmail, setNotifyEmail] = useState(existing?.notifyEmail ?? false);

  useEffect(() => {
    if (nameTouched) return;
    const label = when === "sair" ? "ao SAIR" : when === "entrar" ? "ao ENTRAR" : "ao entrar ou sair";
    setAlertName(`${geofenceName} — ${label}`);
  }, [when, geofenceName, nameTouched]);

  const toggle = (id: string) => {
    vehicleIdsTouchedRef.current = true;
    setVehicleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const filtered = allDevices.filter((v) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      v.name.toLowerCase().includes(s) ||
      v.plate.toLowerCase().includes(s) ||
      (v.driver ?? "").toLowerCase().includes(s)
    );
  });

  const firstSelected = allDevices.find((v) => v.id === vehicleIds[0]);

  return {
    vehicleIds,
    toggle,
    showList,
    setShowList,
    search,
    setSearch,
    when,
    setWhen,
    action,
    setAction,
    alertName,
    setAlertName: (v: string) => {
      setAlertName(v);
      setNameTouched(true);
    },
    notifyApp,
    setNotifyApp,
    notifyEmail,
    setNotifyEmail,
    filtered,
    firstSelected,
  };
}

type GeofenceAlertFormState = ReturnType<typeof useGeofenceAlertFormState>;

// Corpo do formulário (veículos/quando/ação/nome/como avisar) — idêntico nos
// dois lugares onde aparece, só o header/footer ao redor muda.
function GeofenceAlertFormFields({ form, className }: { form: GeofenceAlertFormState; className?: string }) {
  return (
    // min-w-0: <fieldset> por padrão não encolhe abaixo do conteúdo — um nome
    // de veículo comprido na lista alargava o formulário e cortava os botões
    // "ao ENTRAR"/"Desbloquear" pra fora do popup (bug 2026-09-28).
    <fieldset className={cn("min-w-0", className)}>
      {/* Quais veículos */}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Quais veículos?
      </div>
      <button
        type="button"
        onClick={() => form.setShowList((s) => !s)}
        className="mt-2 flex w-full items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-xs text-emerald-800"
      >
        <span className="text-emerald-600">🚗</span>
        <span className="flex-1 truncate">
          {form.vehicleIds.length === 0
            ? "Todos os veículos"
            : `Somente para os veículos selecionados (${form.vehicleIds.length})${
                form.firstSelected ? `: ${form.firstSelected.name} - ${form.firstSelected.plate}` : ""
              }`}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", form.showList && "rotate-180")} />
      </button>

      {form.showList && (
        <div className="mt-2 rounded-md border border-slate-200">
          <div className="relative border-b border-slate-100 p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={form.search}
              onChange={(e) => form.setSearch(e.target.value)}
              placeholder="Buscar placa, modelo ou motorista..."
              className="h-8 w-full rounded-md border border-slate-200 bg-white pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {form.filtered.map((v) => {
              const on = form.vehicleIds.includes(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => form.toggle(v.id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
                >
                  <span
                    className={cn(
                      "grid h-4 w-4 shrink-0 place-items-center rounded border",
                      on
                        ? "border-emerald-500 bg-emerald-500 text-white"
                        : "border-slate-300 bg-white",
                    )}
                  >
                    {on && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-slate-700">{v.name}</span>
                  {v.plate && <LicensePlate plate={v.plate} size="xs" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Quando disparar */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Quando disparar
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {([
          { k: "sair", label: "ao SAIR", icon: "📤" },
          { k: "entrar", label: "ao ENTRAR", icon: "📥" },
          { k: "ambos", label: "ao entrar ou sair", icon: "🔁" },
        ] as { k: TriggerWhen; label: string; icon: string }[]).map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => form.setWhen(o.k)}
            className={cn(
              "flex min-w-0 flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-2 text-center text-[11px] font-semibold leading-tight transition-colors",
              form.when === o.k
                ? "border-emerald-500 bg-emerald-600 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            <span className="text-base leading-none">{o.icon}</span>
            {o.label}
          </button>
        ))}
      </div>

      {/* O que fazer */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        O que fazer
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {([
          { k: "avisar", label: "Só avisar", icon: "🔔" },
          { k: "bloquear", label: "Bloquear", icon: "🔒" },
          { k: "desbloquear", label: "Desbloquear", icon: "🔓" },
        ] as { k: ActionKind; label: string; icon: string }[]).map((o) => (
          <button
            key={o.k}
            type="button"
            onClick={() => form.setAction(o.k)}
            className={cn(
              "flex min-w-0 flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-3 text-center text-[11px] font-semibold leading-tight transition-colors",
              form.action === o.k
                ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-500/20"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            <span className="text-lg leading-none">{o.icon}</span>
            {o.label}
          </button>
        ))}
      </div>

      {/* Nome do alerta */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Nome do alerta
      </div>
      <input
        type="text"
        value={form.alertName}
        onChange={(e) => form.setAlertName(e.target.value)}
        className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
      />

      {/* Como avisar */}
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Como avisar
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => form.setNotifyApp((s) => !s)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors",
            form.notifyApp
              ? "border-emerald-500 bg-emerald-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
          )}
        >
          📱 App
        </button>
        <button
          type="button"
          onClick={() => form.setNotifyEmail((s) => !s)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors",
            form.notifyEmail
              ? "border-emerald-500 bg-emerald-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
          )}
        >
          ✉ E-mail
        </button>
      </div>
    </fieldset>
  );
}

function GeofenceAlertModal({
  geofenceId,
  geofenceName,
  geofenceVehicleIds,
  onClose,
}: {
  geofenceId: number;
  geofenceName: string;
  geofenceVehicleIds: string[];
  onClose: () => void;
}) {
  const { data: allDevices = [] } = useVehicles();
  const form = useGeofenceAlertFormState(geofenceName, allDevices, null, geofenceVehicleIds);
  const createGeofenceRules = useCreateGeofenceAlertRules();

  const handleCreate = () => {
    const deviceIds = resolveGeofenceAlertDeviceIds(form, allDevices);
    if (deviceIds.length === 0) {
      toast.error("Nenhum veículo disponível para vincular a este alerta.");
      return;
    }
    createGeofenceRules.mutate(
      {
        geofenceId,
        deviceIds,
        name: form.alertName.trim() || geofenceName,
        triggerWhen: triggerWhenToApi(form.when),
        actionKind: actionKindToApi(form.action),
        channelApp: form.notifyApp,
        channelEmail: form.notifyEmail,
        dedupeMinutes: 5,
      },
      {
        onSuccess: (rules) => {
          toast.success("Alerta criado", {
            description: `${form.alertName || geofenceName} — ${rules.length} veículo(s) vinculado(s).`,
          });
          onClose();
        },
        onError: (err) =>
          toast.error("Não foi possível criar o alerta", {
            description: err instanceof ApiError ? err.message : undefined,
          }),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 px-4">
      <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rose-100 text-rose-500">
            <MapPin className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-900">Cerca criada!</div>
            <div className="truncate text-xs text-slate-500">{geofenceName}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-100"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <GeofenceAlertFormFields form={form} className="flex-1 overflow-y-auto px-5 py-4" />

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Agora não
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={createGeofenceRules.isPending}
            className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createGeofenceRules.isPending ? "Criando…" : "Sim, me avisar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Mesmo formulário, agora embutido no conteúdo do drawer (troca de tela
// "no lugar", sem sair de Cercas Virtuais) — acionado pelo botão "Criar
// alerta para esta cerca" no painel de detalhes.
function GeofenceAlertPanel({
  geofenceId,
  geofenceName,
  existingRules,
  geofenceVehicleIds,
  onBack,
  onClose,
}: {
  geofenceId: number;
  geofenceName: string;
  existingRules: AlertRule[];
  geofenceVehicleIds: string[];
  onBack: () => void;
  onClose: () => void;
}) {
  const { data: allDevices = [] } = useVehicles();
  const isEditing = existingRules.length > 0;
  const existing = buildExistingGeofenceAlertConfig(existingRules, allDevices);
  const form = useGeofenceAlertFormState(geofenceName, allDevices, existing, geofenceVehicleIds);
  const createGeofenceRules = useCreateGeofenceAlertRules();

  const handleCreate = () => {
    const deviceIds = resolveGeofenceAlertDeviceIds(form, allDevices);
    if (deviceIds.length === 0) {
      toast.error("Nenhum veículo disponível para vincular a este alerta.");
      return;
    }
    createGeofenceRules.mutate(
      {
        geofenceId,
        deviceIds,
        name: form.alertName.trim() || geofenceName,
        triggerWhen: triggerWhenToApi(form.when),
        actionKind: actionKindToApi(form.action),
        channelApp: form.notifyApp,
        channelEmail: form.notifyEmail,
        dedupeMinutes: 5,
      },
      {
        onSuccess: (rules) => {
          toast.success(isEditing ? "Alerta atualizado" : "Alerta criado", {
            description: `${form.alertName || geofenceName} — ${rules.length} veículo(s) vinculado(s).`,
          });
          onBack();
        },
        onError: (err) =>
          toast.error(isEditing ? "Não foi possível atualizar o alerta" : "Não foi possível criar o alerta", {
            description: err instanceof ApiError ? err.message : undefined,
          }),
      },
    );
  };

  return (
    <>
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-rose-50 text-rose-500">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-slate-900">{isEditing ? "Editar alerta" : "Criar alerta"}</h2>
          <p className="truncate text-xs text-slate-500">{geofenceName}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-slate-100"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="border-b border-slate-100 px-5 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          ← Voltar para a cerca "{geofenceName}"
        </button>
      </div>

      <GeofenceAlertFormFields form={form} className="flex-1 overflow-y-auto px-5 py-4" />

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={createGeofenceRules.isPending}
          className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createGeofenceRules.isPending ? "Salvando…" : isEditing ? "Salvar alterações" : "Criar alerta"}
        </button>
      </div>
    </>
  );
}
